<template>
  <v-container fluid class="pa-0">
    <v-alert
      v-if="pendingClosure"
      type="warning"
      dense
      outlined
      class="mb-4"
    >
      <div class="d-flex align-center">
        <div class="flex-grow-1">
          <strong>Cierre pendiente detectado:</strong> {{ pendingClosure.id }}
          <div class="text-caption">
            Una de las dos llamadas put_stock no se completó. Reanuda para emitir
            solo la parte faltante (no se duplicará lo ya emitido).
          </div>
        </div>
        <v-btn small color="warning" @click="resumePendingClosure">
          <v-icon left small>mdi-restart</v-icon>
          Reanudar
        </v-btn>
        <v-btn icon small @click="discardPendingClosure" class="ml-2">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-alert>

    <v-stepper v-model="step" non-linear>
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1" editable>Lotes</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" step="2" :editable="step > 1">
          Refrescar datos
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 3" step="3" :editable="step > 2 && previewReady">
          Preview
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="4" :editable="step > 3">Emisión</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- ========================== PASO 1 — LOTES ========================== -->
        <v-stepper-content step="1">
          <v-card outlined class="pa-4 mb-3">
            <h3 class="text-h6 mb-3">
              <v-icon left>mdi-clipboard-list</v-icon>
              Selecciona los lotes a consolidar en este cierre
            </h3>

            <v-alert v-if="closableBatches.length === 0" type="info" dense outlined>
              No hay lotes abiertos con conteos. Ve a la pestaña "Conteo" y registra
              al menos un producto antes de cerrar.
            </v-alert>

            <div v-else>
              <v-chip-group
                v-model="selectedBatchIds"
                multiple
                column
                active-class="primary--text"
              >
                <v-chip
                  v-for="b in closableBatches"
                  :key="b.id"
                  :value="b.id"
                  filter
                  outlined
                >
                  {{ b.name }} — {{ b.itemCount }} SKUs
                </v-chip>
              </v-chip-group>

              <v-divider class="my-3"></v-divider>

              <v-row dense>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold primary--text">
                      {{ selectedBatchCount }}
                    </div>
                    <div class="text-caption grey--text">Lotes seleccionados</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold">
                      {{ uniqueSkuCount }}
                    </div>
                    <div class="text-caption grey--text">SKUs únicos</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold">
                      {{ totalUnitsCounted }}
                    </div>
                    <div class="text-caption grey--text">Unidades contadas</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center">
                    <div class="text-h6 font-weight-bold info--text">
                      {{ minLastUpdateFormatted }}
                    </div>
                    <div class="text-caption grey--text">Conteo más antiguo</div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>

          <v-btn
            color="primary"
            :disabled="selectedBatchIds.length === 0"
            @click="goToStep2"
          >
            Continuar
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-stepper-content>

        <!-- ========================== PASO 2 — REFRESH ========================== -->
        <v-stepper-content step="2">
          <v-card outlined class="pa-4 mb-3">
            <h3 class="text-h6 mb-3">
              <v-icon left>mdi-cloud-sync</v-icon>
              Refrescar datos del servidor
            </h3>
            <p class="text-caption grey--text mb-4">
              Snapshot de cierre: <strong>{{ closeAtFormatted }}</strong>. Cualquier
              movimiento posterior a este instante quedará para el siguiente cierre.
            </p>

            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center">
                    <v-icon
                      :color="catalogRefreshed ? 'success' : 'grey'"
                      class="mr-3"
                      size="32"
                    >
                      {{ catalogRefreshed ? 'mdi-check-circle' : 'mdi-database' }}
                    </v-icon>
                    <div class="flex-grow-1">
                      <div class="font-weight-medium">1. Inventario sistema actual</div>
                      <div class="text-caption grey--text">
                        {{
                          catalogRefreshed
                            ? `${refreshedInventoryCount} productos cargados`
                            : 'Pendiente'
                        }}
                      </div>
                    </div>
                    <v-btn
                      small
                      color="primary"
                      :loading="loadingCatalog"
                      @click="refreshCatalog"
                    >
                      Refrescar
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center">
                    <v-icon
                      :color="eventsLoaded ? 'success' : 'grey'"
                      class="mr-3"
                      size="32"
                    >
                      {{ eventsLoaded ? 'mdi-check-circle' : 'mdi-history' }}
                    </v-icon>
                    <div class="flex-grow-1">
                      <div class="font-weight-medium">2. Movimientos del periodo</div>
                      <div class="text-caption grey--text">
                        {{
                          eventsLoaded
                            ? `${loadedEventsCount} eventos | ${eventsRangeLabel}`
                            : 'Pendiente'
                        }}
                      </div>
                    </div>
                    <v-btn
                      small
                      color="primary"
                      :loading="loadingEvents"
                      :disabled="!catalogRefreshed"
                      @click="refreshEvents"
                    >
                      Consultar
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-alert
              v-if="refreshError"
              type="error"
              dense
              outlined
              class="mt-3"
            >
              {{ refreshError }}
            </v-alert>
          </v-card>

          <v-btn text @click="step = 1">
            <v-icon left>mdi-chevron-left</v-icon>
            Atrás
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!catalogRefreshed || !eventsLoaded"
            @click="computePreview"
          >
            Calcular preview
            <v-icon right>mdi-calculator</v-icon>
          </v-btn>
        </v-stepper-content>

        <!-- ========================== PASO 3 — PREVIEW ========================== -->
        <v-stepper-content step="3">
          <v-card outlined class="pa-4 mb-3">
            <div class="d-flex align-center mb-3">
              <h3 class="text-h6">
                <v-icon left>mdi-table-eye</v-icon>
                Preview de diferencias
              </h3>
              <v-spacer></v-spacer>
              <v-btn small text @click="recomputePreview">
                <v-icon left small>mdi-refresh</v-icon>
                Recalcular
              </v-btn>
            </div>

            <v-row dense class="mb-3">
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold">{{ summary.skusContados }}</div>
                  <div class="text-caption grey--text">SKUs contados</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold success--text">
                    {{ summary.skusCuadrados }}
                  </div>
                  <div class="text-caption grey--text">Cuadrados</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold error--text">
                    {{ summary.skusFaltante }}
                  </div>
                  <div class="text-caption grey--text">Faltantes</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold warning--text">
                    {{ summary.skusSobrante }}
                  </div>
                  <div class="text-caption grey--text">Sobrantes</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold primary--text">
                    {{ summary.skusAlta }}
                  </div>
                  <div class="text-caption grey--text">Altas (sist.)</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold error--text">
                    ${{ formatNumber(summary.valorFaltante) }}
                  </div>
                  <div class="text-caption grey--text">Valor faltante</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold warning--text">
                    ${{ formatNumber(summary.valorSobrante) }}
                  </div>
                  <div class="text-caption grey--text">Valor sobrante</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold primary--text">
                    ${{ formatNumber(summary.valorAlta) }}
                  </div>
                  <div class="text-caption grey--text">Valor alta</div>
                </div>
              </v-col>
            </v-row>

            <v-btn-toggle v-model="rowFilter" mandatory dense class="mb-3">
              <v-btn small value="all">Todos ({{ rows.length }})</v-btn>
              <v-btn small value="diff">Con diferencia</v-btn>
              <v-btn small value="out">Faltantes</v-btn>
              <v-btn small value="in">Sobrantes</v-btn>
              <v-btn small value="alta">Altas</v-btn>
              <v-btn small value="warn">Con warning</v-btn>
            </v-btn-toggle>

            <v-data-table
              :headers="rowHeaders"
              :items="filteredRows"
              :items-per-page="20"
              show-expand
              single-expand
              :expanded.sync="expandedRows"
              dense
              class="elevation-1"
            >
              <template v-slot:item.action="{ item }">
                <v-chip
                  x-small
                  :color="actionColor(item.action)"
                  text-color="white"
                  v-if="item.action !== 'none'"
                >
                  {{ actionLabel(item.action) }}
                </v-chip>
                <span v-else class="grey--text text-caption">cuadrado</span>
              </template>

              <template v-slot:item.countedQuantity="{ item }">
                {{ formatNumber(item.countedQuantity, 0) }}
              </template>
              <template v-slot:item.systemQuantityNow="{ item }">
                {{
                  item.systemQuantityNow == null
                    ? '—'
                    : formatNumber(item.systemQuantityNow, 0)
                }}
              </template>
              <template v-slot:item.movsOut="{ item }">
                <span :class="item.movsOut > 0 ? 'error--text' : 'grey--text'">
                  {{ formatNumber(item.movsOut, 0) }}
                </span>
              </template>
              <template v-slot:item.movsIn="{ item }">
                <span :class="item.movsIn > 0 ? 'success--text' : 'grey--text'">
                  {{ formatNumber(item.movsIn, 0) }}
                </span>
              </template>
              <template v-slot:item.expected="{ item }">
                {{ item.expected == null ? '—' : formatNumber(item.expected, 0) }}
              </template>
              <template v-slot:item.difference="{ item }">
                <span
                  :class="
                    item.difference == null
                      ? 'grey--text'
                      : item.difference > 0
                      ? 'error--text font-weight-bold'
                      : item.difference < 0
                      ? 'warning--text font-weight-bold'
                      : 'success--text'
                  "
                >
                  {{ item.difference == null ? '—' : formatNumber(item.difference, 0) }}
                </span>
              </template>
              <template v-slot:item.valuation="{ item }">
                ${{ formatNumber(item.valuation) }}
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="grey lighten-5 pa-3">
                  <div v-if="item.warning" class="error--text mb-2">
                    <v-icon small color="error">mdi-alert</v-icon>
                    {{ item.warning }}
                  </div>
                  <div class="text-caption">
                    <strong>Conteo registrado:</strong>
                    {{ formatDate(item.countedAt) }} en lotes
                    {{ (item.batchIds || []).join(', ') }}
                    | Stock al scan: {{ formatNumber(item.systemQuantityInitial, 0) }}
                  </div>
                  <div class="mt-2">
                    <strong>Movimientos posteriores ({{ item.eventCount }}):</strong>
                    <div v-if="item.eventCount === 0" class="grey--text">
                      Sin movimientos en el periodo.
                    </div>
                    <div v-else>
                      <v-chip
                        v-for="(qty, code) in item.movsByCode"
                        :key="code"
                        x-small
                        outlined
                        class="mr-1 mt-1"
                      >
                        {{ code }}: {{ formatNumber(qty, 0) }}
                      </v-chip>
                    </div>
                  </div>
                  <div class="mt-2 text-caption grey--text">
                    Fórmula: esperado = {{ formatNumber(item.countedQuantity, 0) }}
                    − {{ formatNumber(item.movsOut, 0) }}
                    + {{ formatNumber(item.movsIn, 0) }}
                    = {{ item.expected == null ? '—' : formatNumber(item.expected, 0) }}
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-card>

          <v-btn text @click="step = 2">
            <v-icon left>mdi-chevron-left</v-icon>
            Atrás
          </v-btn>
          <v-btn
            color="primary"
            :disabled="actionableRowsCount === 0"
            @click="step = 4"
          >
            Continuar a emisión ({{ actionableRowsCount }} ajustes)
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
          <v-alert
            v-if="actionableRowsCount === 0"
            type="success"
            dense
            outlined
            class="mt-3 mb-0"
          >
            Todos los SKUs cuadran. No hay ajustes que emitir; puedes simplemente
            marcar los lotes como auditados.
            <v-btn x-small color="success" @click="closeWithoutAdjustment" class="ml-2">
              Marcar lotes como auditados
            </v-btn>
          </v-alert>
        </v-stepper-content>

        <!-- ========================== PASO 4 — EMISIÓN ========================== -->
        <v-stepper-content step="4">
          <v-card outlined class="pa-4 mb-3">
            <h3 class="text-h6 mb-3">
              <v-icon left>mdi-shield-check</v-icon>
              Autorización y emisión de ajustes
            </h3>

            <v-alert v-if="!selectedAuthorizer" type="info" dense outlined class="mb-3">
              Selecciona un usuario autorizador (rol ADMIN u OWNER) y valida con
              contraseña antes de emitir los ajustes.
            </v-alert>

            <v-row align="center">
              <v-col cols="12" md="8">
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center">
                    <v-icon
                      :color="selectedAuthorizer ? 'success' : 'grey'"
                      class="mr-3"
                      size="32"
                    >
                      mdi-account-check
                    </v-icon>
                    <div class="flex-grow-1">
                      <div class="font-weight-medium">
                        {{
                          selectedAuthorizer
                            ? selectedAuthorizer.user_name ||
                              selectedAuthorizer.user_email
                            : 'Sin autorizador'
                        }}
                      </div>
                      <div v-if="selectedAuthorizer" class="text-caption grey--text">
                        {{ selectedAuthorizer.user_email }} —
                        {{ selectedAuthorizer.user_access }}
                      </div>
                    </div>
                    <v-btn small @click="openAuthorizerDialog" :loading="loadingUsers">
                      <v-icon left small>mdi-account-switch</v-icon>
                      {{ selectedAuthorizer ? 'Cambiar' : 'Seleccionar' }}
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold primary--text">
                    {{ actionableRowsCount }}
                  </div>
                  <div class="text-caption grey--text">SKUs a ajustar</div>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-3"></v-divider>

            <div class="d-flex align-center">
              <div>
                <div class="text-caption grey--text">stockOut (faltantes)</div>
                <div class="text-h6">
                  {{ summary.skusFaltante }} SKUs · ${{ formatNumber(summary.valorFaltante) }}
                </div>
              </div>
              <v-divider vertical class="mx-4"></v-divider>
              <div>
                <div class="text-caption grey--text">stockIn (sobrantes)</div>
                <div class="text-h6">
                  {{ summary.skusSobrante }} SKUs · ${{ formatNumber(summary.valorSobrante) }}
                </div>
              </div>
              <template v-if="summary.skusAlta">
                <v-divider vertical class="mx-4"></v-divider>
                <div>
                  <div class="text-caption grey--text">alta inicial (sistematización)</div>
                  <div class="text-h6 primary--text">
                    {{ summary.skusAlta }} SKUs · ${{ formatNumber(summary.valorAlta) }}
                  </div>
                </div>
              </template>
            </div>

            <v-alert
              v-if="executionResult"
              :type="executionResult.partial ? 'warning' : 'success'"
              dense
              outlined
              class="mt-3"
            >
              <div v-if="executionResult.partial">
                <strong>Cierre parcial</strong> — revisa el cierre pendiente y reanuda.
              </div>
              <div v-else>
                <strong>Cierre completado:</strong>
                <div
                  v-for="ref in executionResult.adjustmentRefs"
                  :key="ref.type"
                  class="text-caption"
                >
                  {{ ref.type }}: doc <strong>{{ ref.doc_number || '—' }}</strong>
                  ({{ ref.itemCount }} items · ${{ formatNumber(ref.doc_value) }})
                </div>
              </div>
            </v-alert>
          </v-card>

          <v-btn text @click="step = 3" :disabled="executing">
            <v-icon left>mdi-chevron-left</v-icon>
            Atrás
          </v-btn>
          <v-btn
            color="error"
            :disabled="!selectedAuthorizer || actionableRowsCount === 0"
            :loading="executing"
            @click="executeClosure"
          >
            <v-icon left>mdi-check-bold</v-icon>
            Generar ajustes
          </v-btn>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <!-- ============== Diálogo de selección de autorizador ============== -->
    <v-dialog v-model="authorizerDialog" max-width="600px">
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="primary">
            <v-icon class="mr-3">mdi-account-check</v-icon>
            <v-toolbar-title>Seleccionar Autorizador</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="authorizerDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-list v-if="authorizedUsers.length > 0">
            <v-list-item
              v-for="user in authorizedUsers"
              :key="user.user_email"
              @click="selectAuthorizerForValidation(user)"
            >
              <v-list-item-avatar>
                <v-icon :color="user.user_access === 'OWNER' ? 'primary' : 'success'">
                  {{ user.user_access === 'OWNER' ? 'mdi-crown' : 'mdi-shield-account' }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ user.user_name || user.user_email }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ user.user_email }}
                  <v-chip
                    x-small
                    class="ml-2"
                    :color="user.user_access === 'OWNER' ? 'primary' : 'success'"
                  >
                    {{ user.user_access }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="text-center py-12">
            <v-progress-circular
              v-if="loadingUsers"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <template v-else>
              <v-icon size="64" color="grey lighten-2">mdi-account-off</v-icon>
              <h3 class="text-h6 mt-4 grey--text">Sin usuarios autorizados</h3>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ============== Diálogo de validación de password ============== -->
    <v-dialog v-model="passwordDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="secondary">
            <v-icon class="mr-3">mdi-lock-check</v-icon>
            <v-toolbar-title>Confirmar Autorización</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="text-center mb-4">
            <v-icon size="64" color="secondary">mdi-shield-lock</v-icon>
            <p class="text-body-2 grey--text mt-2">
              Contraseña de
              <strong>{{
                tempAuthorizer
                  ? tempAuthorizer.user_name || tempAuthorizer.user_email
                  : ''
              }}</strong>
            </p>
          </div>
          <v-text-field
            v-model="authorizerPassword"
            label="Contraseña"
            type="password"
            outlined
            prepend-inner-icon="mdi-lock"
            :error-messages="passwordError"
            @input="passwordError = ''"
            @keyup.enter="validatePassword"
            autofocus
          />
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelPasswordValidation">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="validatePassword"
            :loading="validatingPassword"
            :disabled="!authorizerPassword"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      bottom
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver } from '@/services/webserver.js';
import {
  aggregateAuditsAcrossBatches,
  groupEventsByEan,
  buildClosurePreview,
  buildPutStockPayloads,
  indexInventoryByEan,
} from '../utils/closureEngine.js';

export default {
  name: 'AuditClosure',

  props: {
    batches: { type: Array, required: true },
    inventory: { type: Array, required: true },
    auditDB: { type: Object, required: true },
  },

  data() {
    return {
      step: 1,
      selectedBatchIds: [],

      // Refresh
      closeAt: null,
      catalogRefreshed: false,
      loadingCatalog: false,
      refreshedInventory: [],
      refreshedInventoryCount: 0,
      eventsLoaded: false,
      loadingEvents: false,
      loadedEvents: [],
      loadedEventsCount: 0,
      eventsRange: { dateFrom: null, dateTo: null },
      refreshError: '',

      // Preview
      rows: [],
      summary: {
        skusContados: 0,
        skusCuadrados: 0,
        skusFaltante: 0,
        skusSobrante: 0,
        skusAlta: 0,
        totalFaltanteUnidades: 0,
        totalSobranteUnidades: 0,
        totalAltaUnidades: 0,
        valorFaltante: 0,
        valorSobrante: 0,
        valorAlta: 0,
      },
      warnings: [],
      previewReady: false,
      rowFilter: 'all',
      expandedRows: [],

      // Authorizer
      users: [],
      loadingUsers: false,
      authorizerDialog: false,
      passwordDialog: false,
      tempAuthorizer: null,
      selectedAuthorizer: null,
      authorizerPassword: '',
      passwordError: '',
      validatingPassword: false,

      // Execution
      executing: false,
      executionResult: null,
      pendingClosure: null,

      snackbar: { show: false, text: '', color: 'success', timeout: 3000 },

      rowHeaders: [
        { text: '', value: 'data-table-expand', sortable: false, width: 30 },
        { text: 'Acción', value: 'action', width: 90 },
        { text: 'EAN', value: 'ean' },
        { text: 'Producto', value: 'nombre' },
        { text: 'Contado', value: 'countedQuantity', align: 'end' },
        { text: 'Sistema actual', value: 'systemQuantityNow', align: 'end' },
        { text: 'Salidas post', value: 'movsOut', align: 'end' },
        { text: 'Entradas post', value: 'movsIn', align: 'end' },
        { text: 'Esperado', value: 'expected', align: 'end' },
        { text: 'Diferencia', value: 'difference', align: 'end' },
        { text: 'Valor', value: 'valuation', align: 'end' },
      ],
    };
  },

  computed: {
    closableBatches() {
      return (this.batches || []).filter(
        (b) => b.status === 'open' && b.itemCount > 0
      );
    },
    selectedBatches() {
      const ids = new Set(this.selectedBatchIds);
      return this.closableBatches.filter((b) => ids.has(b.id));
    },
    selectedBatchCount() {
      return this.selectedBatchIds.length;
    },
    aggregated() {
      if (this.selectedBatches.length === 0) {
        return { byEan: new Map(), sourceBatchIds: [] };
      }
      return aggregateAuditsAcrossBatches(this.selectedBatches);
    },
    uniqueSkuCount() {
      return this.aggregated.byEan.size;
    },
    totalUnitsCounted() {
      let total = 0;
      for (const v of this.aggregated.byEan.values()) total += v.countedQuantity;
      return total;
    },
    minLastUpdate() {
      let min = null;
      for (const v of this.aggregated.byEan.values()) {
        if (v.lastUpdate && (!min || v.lastUpdate < min)) min = v.lastUpdate;
      }
      return min;
    },
    minLastUpdateFormatted() {
      return this.minLastUpdate ? this.formatDate(this.minLastUpdate) : '—';
    },
    closeAtFormatted() {
      return this.closeAt ? this.formatDate(this.closeAt) : '—';
    },
    eventsRangeLabel() {
      if (!this.eventsRange.dateFrom) return '';
      return `${this.eventsRange.dateFrom} → ${this.eventsRange.dateTo}`;
    },
    authorizedUsers() {
      return (this.users || []).filter((u) =>
        ['ADMIN', 'OWNER'].includes(u.user_access)
      );
    },
    filteredRows() {
      switch (this.rowFilter) {
        case 'diff':
          return this.rows.filter((r) => r.action !== 'none');
        case 'out':
          return this.rows.filter((r) => r.action === 'out');
        case 'in':
          return this.rows.filter((r) => r.action === 'in');
        case 'alta':
          return this.rows.filter((r) => r.action === 'alta');
        case 'warn':
          return this.rows.filter((r) => !!r.warning);
        default:
          return this.rows;
      }
    },
    actionableRowsCount() {
      return this.rows.filter((r) => r.action !== 'none').length;
    },
  },

  async mounted() {
    // Default: pre-seleccionar todos los lotes cerrables.
    this.selectedBatchIds = this.closableBatches.map((b) => b.id);
    await this.detectPendingClosure();
    this.getUsers();
  },

  watch: {
    batches() {
      // Si el padre recarga lotes, mantener solo los todavía cerrables.
      const closableIds = new Set(this.closableBatches.map((b) => b.id));
      this.selectedBatchIds = this.selectedBatchIds.filter((id) => closableIds.has(id));
      if (this.selectedBatchIds.length === 0 && this.closableBatches.length > 0) {
        this.selectedBatchIds = this.closableBatches.map((b) => b.id);
      }
    },
  },

  methods: {
    async detectPendingClosure() {
      try {
        const closures = await this.auditDB.getClosures();
        this.pendingClosure = (closures || []).find((c) => c.partial === true) || null;
      } catch (err) {
        console.error('Error detectando cierres pendientes:', err);
      }
    },

    goToStep2() {
      this.closeAt = new Date();
      this.catalogRefreshed = false;
      this.eventsLoaded = false;
      this.refreshError = '';
      this.step = 2;
    },

    refreshCatalog() {
      this.loadingCatalog = true;
      this.refreshError = '';
      const qry = {
        store: window.store.store_id,
        table: 'items',
        filters: [
          { field: 'id_pos', operator: '=', value: window.store.store_id },
        ],
      };
      webserver('get_catalog', qry, (data) => {
        this.loadingCatalog = false;
        if (data && data.error) {
          this.refreshError = 'Error al refrescar inventario: ' + data.message;
          return;
        }
        if (!Array.isArray(data)) {
          this.refreshError = 'Respuesta inválida del catálogo';
          return;
        }
        this.refreshedInventory = data;
        this.refreshedInventoryCount = data.length;
        this.catalogRefreshed = true;
      });
    },

    refreshEvents() {
      this.loadingEvents = true;
      this.refreshError = '';

      // Rango con buffer (get_events filtra por día). dateFrom = 1 día antes
      // del lastUpdate más antiguo; dateTo = closeAt + 1 día. Filtrado fino
      // por timestamp se hace client-side en computeNetMovementsByEan.
      const minLU = this.minLastUpdate || this.closeAt;
      const dateFrom = this.toYMD(this.addDays(minLU, -1));
      const dateTo = this.toYMD(this.addDays(this.closeAt, 1));

      const qry = {
        store: window.store.store_id,
        dateFrom,
        dateTo,
        events:
          "'OP','OC','AJ','stockIn','stockOut','G-IN','G-OUT','PS','PE','DV','CD','TB','SI','SO','SAL','GI','GS'",
      };
      this.eventsRange = { dateFrom, dateTo };

      webserver('get_events', qry, (data) => {
        this.loadingEvents = false;
        if (data && data.error) {
          this.refreshError = 'Error al consultar movimientos: ' + data.message;
          return;
        }
        if (!Array.isArray(data)) {
          this.refreshError = 'Respuesta inválida de get_events';
          return;
        }
        this.loadedEvents = data;
        this.loadedEventsCount = data.length;
        this.eventsLoaded = true;
      });
    },

    computePreview() {
      const inv = indexInventoryByEan(this.refreshedInventory);
      const masterByEan = indexInventoryByEan(this.inventory);
      const eventsByEan = groupEventsByEan(this.loadedEvents);
      const { rows, summary, warnings } = buildClosurePreview({
        aggregatedAudits: this.aggregated.byEan,
        currentInventoryByEan: inv,
        eventsByEan,
        closeAt: this.closeAt,
        masterByEan,
      });
      this.rows = rows;
      this.summary = summary;
      this.warnings = warnings;
      this.previewReady = true;
      this.step = 3;
    },

    recomputePreview() {
      this.computePreview();
      this.showMessage('Preview recalculada', 'info');
    },

    actionColor(action) {
      if (action === 'out') return 'error';
      if (action === 'in') return 'warning';
      if (action === 'alta') return 'primary';
      return 'grey';
    },

    actionLabel(action) {
      if (action === 'out') return 'Faltante';
      if (action === 'in') return 'Sobrante';
      if (action === 'alta') return 'Alta';
      return 'OK';
    },

    // ============== Authorizer + password (patrón de IAOut.vue) ==============
    getUsers() {
      const qry = {
        store: window.profile.store_id,
        table: 'users',
        filters: [
          { field: 'store_id', operator: '=', value: window.profile.store_id },
          { field: 'user_access', operator: ' IN ', value: "'ADMIN','OWNER'" },
        ],
      };
      this.loadingUsers = true;
      webserver('get_table', qry, (data) => {
        this.loadingUsers = false;
        if (Array.isArray(data)) this.users = data;
      });
    },

    openAuthorizerDialog() {
      this.authorizerDialog = true;
      if (this.users.length === 0) this.getUsers();
    },

    selectAuthorizerForValidation(user) {
      this.tempAuthorizer = user;
      this.authorizerPassword = '';
      this.passwordError = '';
      this.passwordDialog = true;
    },

    cancelPasswordValidation() {
      this.passwordDialog = false;
      this.authorizerPassword = '';
      this.passwordError = '';
      this.tempAuthorizer = null;
    },

    validatePassword() {
      if (!this.authorizerPassword) {
        this.passwordError = 'La contraseña es requerida';
        return;
      }
      this.validatingPassword = true;
      const md5 = this.md5(this.authorizerPassword);
      if (this.tempAuthorizer && this.tempAuthorizer.user_password === md5) {
        this.selectedAuthorizer = this.tempAuthorizer;
        this.passwordDialog = false;
        this.authorizerDialog = false;
        this.tempAuthorizer = null;
        this.authorizerPassword = '';
        this.showMessage('Autorización confirmada', 'success');
      } else {
        this.passwordError = 'Contraseña incorrecta';
        this.authorizerPassword = '';
      }
      this.validatingPassword = false;
    },

    // ============== Ejecución del cierre ==============
    async closeWithoutAdjustment() {
      const closureId = 'CL-' + Date.now();
      const batchIds = this.selectedBatchIds.slice();
      const closure = {
        id: closureId,
        runAt: new Date().toISOString(),
        closeAt: (this.closeAt || new Date()).toISOString(),
        store: window.store.store_id,
        userId: window.profile ? window.profile.user_email : null,
        authorizerEmail: null,
        batchIds,
        eventsQueryRange: this.eventsRange,
        eventCount: this.loadedEventsCount,
        rows: this.rows,
        summary: this.summary,
        adjustmentRefs: [],
        partial: false,
        errorLog: [],
      };
      await this.auditDB.saveClosure(closure);
      await this.auditDB.markBatchesAsAudited(batchIds, closureId);
      this.executionResult = closure;
      this.showMessage('Lotes marcados como auditados (sin diferencias)', 'success');
      this.$emit('closure-completed', closure);
    },

    async executeClosure() {
      if (!this.selectedAuthorizer) {
        this.showMessage('Selecciona un autorizador', 'warning');
        return;
      }
      if (this.actionableRowsCount === 0) {
        this.showMessage('No hay filas con diferencia que ajustar', 'info');
        return;
      }

      this.executing = true;

      const closureId = 'CL-' + Date.now();
      const batchIds = this.selectedBatchIds.slice();
      const closeAtIso = (this.closeAt || new Date()).toISOString();

      const { stockOutPayload, stockInPayload, stockInitialPayload } =
        buildPutStockPayloads(this.rows, {
          store: window.store.store_id,
          userId: window.profile ? window.profile.user_email : null,
          seller: window.seller,
          authorizer: this.selectedAuthorizer,
          closureId,
        });

      const adjustmentRefs = [];
      if (stockOutPayload) {
        adjustmentRefs.push({
          type: 'stockOut',
          doc_number: null,
          doc_value: stockOutPayload.doc.doc_value,
          itemCount: stockOutPayload.items.length,
          status: 'pending',
        });
      }
      if (stockInPayload) {
        adjustmentRefs.push({
          type: 'stockIn',
          doc_number: null,
          doc_value: stockInPayload.doc.doc_value,
          itemCount: stockInPayload.items.length,
          status: 'pending',
        });
      }
      if (stockInitialPayload) {
        adjustmentRefs.push({
          type: 'stockInitial',
          doc_number: null,
          doc_value: stockInitialPayload.doc.doc_value,
          itemCount: stockInitialPayload.items.length,
          status: 'pending',
        });
      }

      const closure = {
        id: closureId,
        runAt: new Date().toISOString(),
        closeAt: closeAtIso,
        store: window.store.store_id,
        userId: window.profile ? window.profile.user_email : null,
        authorizerEmail: this.selectedAuthorizer.user_email,
        batchIds,
        eventsQueryRange: this.eventsRange,
        eventCount: this.loadedEventsCount,
        rows: this.rows,
        summary: this.summary,
        adjustmentRefs,
        partial: true,
        errorLog: [],
      };

      // Snapshot inicial — si todo falla queda evidencia
      await this.auditDB.saveClosure(closure);

      try {
        // 1) stockOut primero (faltantes)
        if (stockOutPayload) {
          const resp = await this.callPutStock(stockOutPayload);
          const ref = adjustmentRefs.find((r) => r.type === 'stockOut');
          if (resp && !resp.error) {
            ref.doc_number = this.extractDocNumber(resp);
            ref.status = 'ok';
          } else {
            ref.status = 'error';
            closure.errorLog.push({ type: 'stockOut', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.executionResult = closure;
            this.executing = false;
            this.showMessage('Falló stockOut: ' + (resp && resp.message), 'error');
            return;
          }
          await this.auditDB.saveClosure(closure);
        }

        // 2) stockIn (sobrantes)
        if (stockInPayload) {
          const resp = await this.callPutStock(stockInPayload);
          const ref = adjustmentRefs.find((r) => r.type === 'stockIn');
          if (resp && !resp.error) {
            ref.doc_number = this.extractDocNumber(resp);
            ref.status = 'ok';
          } else {
            ref.status = 'error';
            closure.errorLog.push({ type: 'stockIn', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.executionResult = closure;
            this.executing = false;
            this.showMessage(
              'stockOut emitido pero falló stockIn. Cierre quedó parcial.',
              'warning'
            );
            return;
          }
          await this.auditDB.saveClosure(closure);
        }

        // 3) stockInitial (alta inicial — productos sin inventario por tienda)
        if (stockInitialPayload) {
          const resp = await this.callPutStock(stockInitialPayload);
          const ref = adjustmentRefs.find((r) => r.type === 'stockInitial');
          if (resp && !resp.error) {
            ref.doc_number = this.extractDocNumber(resp);
            ref.status = 'ok';
          } else {
            ref.status = 'error';
            closure.errorLog.push({ type: 'stockInitial', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.executionResult = closure;
            this.executing = false;
            this.showMessage(
              'Falló el alta inicial. Cierre quedó parcial.',
              'warning'
            );
            return;
          }
        }

        // 4) Marcar lotes como auditados
        closure.partial = false;
        await this.auditDB.saveClosure(closure);
        await this.auditDB.markBatchesAsAudited(batchIds, closureId);

        this.executionResult = closure;
        this.pendingClosure = null;
        this.showMessage('Cierre completado: ' + closureId, 'success');
        this.$emit('closure-completed', closure);
      } catch (err) {
        closure.errorLog.push({ type: 'exception', message: String(err) });
        await this.auditDB.saveClosure(closure);
        this.executionResult = closure;
        this.showMessage('Excepción durante cierre: ' + err, 'error');
      } finally {
        this.executing = false;
      }
    },

    callPutStock(payload) {
      return new Promise((resolve) => {
        webserver('put_stock', payload, (resp) => resolve(resp));
      });
    },

    extractDocNumber(resp) {
      if (!resp || typeof resp !== 'object') return null;
      return (
        resp.doc_number ||
        resp.docNumber ||
        (resp.doc && resp.doc.doc_number) ||
        (resp.data && resp.data.doc_number) ||
        null
      );
    },

    async resumePendingClosure() {
      const closure = this.pendingClosure;
      if (!closure) return;

      this.executing = true;
      try {
        // Reconstruir payloads pendientes desde rows persistidas
        const { stockOutPayload, stockInPayload, stockInitialPayload } =
          buildPutStockPayloads(closure.rows || [], {
            store: closure.store,
            userId: closure.userId,
            seller: window.seller,
            authorizer: this.selectedAuthorizer || {
              user_email: closure.authorizerEmail,
            },
            closureId: closure.id,
          });

        const refOut = (closure.adjustmentRefs || []).find((r) => r.type === 'stockOut');
        const refIn = (closure.adjustmentRefs || []).find((r) => r.type === 'stockIn');
        const refInit = (closure.adjustmentRefs || []).find((r) => r.type === 'stockInitial');

        if (refOut && refOut.status !== 'ok' && stockOutPayload) {
          const resp = await this.callPutStock(stockOutPayload);
          if (resp && !resp.error) {
            refOut.doc_number = this.extractDocNumber(resp);
            refOut.status = 'ok';
          } else {
            refOut.status = 'error';
            closure.errorLog.push({ type: 'stockOut-resume', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.showMessage('Reanudación de stockOut falló', 'error');
            this.executing = false;
            return;
          }
          await this.auditDB.saveClosure(closure);
        }

        if (refIn && refIn.status !== 'ok' && stockInPayload) {
          const resp = await this.callPutStock(stockInPayload);
          if (resp && !resp.error) {
            refIn.doc_number = this.extractDocNumber(resp);
            refIn.status = 'ok';
          } else {
            refIn.status = 'error';
            closure.errorLog.push({ type: 'stockIn-resume', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.showMessage('Reanudación de stockIn falló', 'error');
            this.executing = false;
            return;
          }
          await this.auditDB.saveClosure(closure);
        }

        if (refInit && refInit.status !== 'ok' && stockInitialPayload) {
          const resp = await this.callPutStock(stockInitialPayload);
          if (resp && !resp.error) {
            refInit.doc_number = this.extractDocNumber(resp);
            refInit.status = 'ok';
          } else {
            refInit.status = 'error';
            closure.errorLog.push({ type: 'stockInitial-resume', message: resp && resp.message });
            await this.auditDB.saveClosure(closure);
            this.showMessage('Reanudación de alta inicial falló', 'error');
            this.executing = false;
            return;
          }
        }

        const allOk = (closure.adjustmentRefs || []).every((r) => r.status === 'ok');
        if (allOk) {
          closure.partial = false;
          await this.auditDB.saveClosure(closure);
          await this.auditDB.markBatchesAsAudited(closure.batchIds, closure.id);
          this.pendingClosure = null;
          this.executionResult = closure;
          this.showMessage('Cierre pendiente reanudado y completado', 'success');
          this.$emit('closure-completed', closure);
        }
      } catch (err) {
        this.showMessage('Excepción reanudando cierre: ' + err, 'error');
      } finally {
        this.executing = false;
      }
    },

    async discardPendingClosure() {
      if (!this.pendingClosure) return;
      const id = this.pendingClosure.id;
      this.pendingClosure.discarded = true;
      this.pendingClosure.partial = false;
      await this.auditDB.saveClosure(this.pendingClosure);
      this.pendingClosure = null;
      this.showMessage('Cierre pendiente descartado: ' + id, 'info');
    },

    // ============== Helpers ==============
    addDays(date, n) {
      const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
      d.setDate(d.getDate() + n);
      return d;
    },

    toYMD(date) {
      const d = date instanceof Date ? date : new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatNumber(value, decimals = 2) {
      if (value == null || isNaN(value)) return '0';
      return Number(value).toLocaleString('es-ES', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },

    showMessage(text, color = 'success') {
      this.snackbar = { show: true, text, color, timeout: 3500 };
    },

    // MD5 (idéntico a IAOut.vue:900-)
    md5(string) {
      function md5cycle(x, k) {
        let a = x[0], b = x[1], c = x[2], d = x[3];
        a = ff(a, b, c, d, k[0], 7, -680876936); d = ff(d, a, b, c, k[1], 12, -389564586);
        c = ff(c, d, a, b, k[2], 17, 606105819); b = ff(b, c, d, a, k[3], 22, -1044525330);
        a = ff(a, b, c, d, k[4], 7, -176418897); d = ff(d, a, b, c, k[5], 12, 1200080426);
        c = ff(c, d, a, b, k[6], 17, -1473231341); b = ff(b, c, d, a, k[7], 22, -45705983);
        a = ff(a, b, c, d, k[8], 7, 1770035416); d = ff(d, a, b, c, k[9], 12, -1958414417);
        c = ff(c, d, a, b, k[10], 17, -42063); b = ff(b, c, d, a, k[11], 22, -1990404162);
        a = ff(a, b, c, d, k[12], 7, 1804603682); d = ff(d, a, b, c, k[13], 12, -40341101);
        c = ff(c, d, a, b, k[14], 17, -1502002290); b = ff(b, c, d, a, k[15], 22, 1236535329);
        a = gg(a, b, c, d, k[1], 5, -165796510); d = gg(d, a, b, c, k[6], 9, -1069501632);
        c = gg(c, d, a, b, k[11], 14, 643717713); b = gg(b, c, d, a, k[0], 20, -373897302);
        a = gg(a, b, c, d, k[5], 5, -701558691); d = gg(d, a, b, c, k[10], 9, 38016083);
        c = gg(c, d, a, b, k[15], 14, -660478335); b = gg(b, c, d, a, k[4], 20, -405537848);
        a = gg(a, b, c, d, k[9], 5, 568446438); d = gg(d, a, b, c, k[14], 9, -1019803690);
        c = gg(c, d, a, b, k[3], 14, -187363961); b = gg(b, c, d, a, k[8], 20, 1163531501);
        a = gg(a, b, c, d, k[13], 5, -1444681467); d = gg(d, a, b, c, k[2], 9, -51403784);
        c = gg(c, d, a, b, k[7], 14, 1735328473); b = gg(b, c, d, a, k[12], 20, -1926607734);
        a = hh(a, b, c, d, k[5], 4, -378558); d = hh(d, a, b, c, k[8], 11, -2022574463);
        c = hh(c, d, a, b, k[11], 16, 1839030562); b = hh(b, c, d, a, k[14], 23, -35309556);
        a = hh(a, b, c, d, k[1], 4, -1530992060); d = hh(d, a, b, c, k[4], 11, 1272893353);
        c = hh(c, d, a, b, k[7], 16, -155497632); b = hh(b, c, d, a, k[10], 23, -1094730640);
        a = hh(a, b, c, d, k[13], 4, 681279174); d = hh(d, a, b, c, k[0], 11, -358537222);
        c = hh(c, d, a, b, k[3], 16, -722521979); b = hh(b, c, d, a, k[6], 23, 76029189);
        a = hh(a, b, c, d, k[9], 4, -640364487); d = hh(d, a, b, c, k[12], 11, -421815835);
        c = hh(c, d, a, b, k[15], 16, 530742520); b = hh(b, c, d, a, k[2], 23, -995338651);
        a = ii(a, b, c, d, k[0], 6, -198630844); d = ii(d, a, b, c, k[7], 10, 1126891415);
        c = ii(c, d, a, b, k[14], 15, -1416354905); b = ii(b, c, d, a, k[5], 21, -57434055);
        a = ii(a, b, c, d, k[12], 6, 1700485571); d = ii(d, a, b, c, k[3], 10, -1894986606);
        c = ii(c, d, a, b, k[10], 15, -1051523); b = ii(b, c, d, a, k[1], 21, -2054922799);
        a = ii(a, b, c, d, k[8], 6, 1873313359); d = ii(d, a, b, c, k[15], 10, -30611744);
        c = ii(c, d, a, b, k[6], 15, -1560198380); b = ii(b, c, d, a, k[13], 21, 1309151649);
        a = ii(a, b, c, d, k[4], 6, -145523070); d = ii(d, a, b, c, k[11], 10, -1120210379);
        c = ii(c, d, a, b, k[2], 15, 718787259); b = ii(b, c, d, a, k[9], 21, -343485551);
        x[0] = add32(a, x[0]); x[1] = add32(b, x[1]);
        x[2] = add32(c, x[2]); x[3] = add32(d, x[3]);
      }
      function cmn(q, a, b, x, s, t) {
        a = add32(add32(a, q), add32(x, t));
        return add32((a << s) | (a >>> (32 - s)), b);
      }
      function ff(a, b, c, d, x, s, t) { return cmn((b & c) | (~b & d), a, b, x, s, t); }
      function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & ~d), a, b, x, s, t); }
      function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t); }
      function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | ~d), a, b, x, s, t); }
      function md51(s) {
        const n = s.length;
        const state = [1732584193, -271733879, -1732584194, 271733878];
        let i;
        for (i = 64; i <= s.length; i += 64) md5cycle(state, md5blk(s.substring(i - 64, i)));
        s = s.substring(i - 64);
        const tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        for (i = 0; i < s.length; i++) tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
        tail[i >> 2] |= 0x80 << ((i % 4) << 3);
        if (i > 55) { md5cycle(state, tail); for (i = 0; i < 16; i++) tail[i] = 0; }
        tail[14] = n * 8;
        md5cycle(state, tail);
        return state;
      }
      function md5blk(s) {
        const md5blks = [];
        for (let i = 0; i < 64; i += 4) {
          md5blks[i >> 2] = s.charCodeAt(i) + (s.charCodeAt(i + 1) << 8) +
            (s.charCodeAt(i + 2) << 16) + (s.charCodeAt(i + 3) << 24);
        }
        return md5blks;
      }
      const hex_chr = '0123456789abcdef'.split('');
      function rhex(n) {
        let s = '';
        for (let j = 0; j < 4; j++) {
          s += hex_chr[(n >> (j * 8 + 4)) & 0x0f] + hex_chr[(n >> (j * 8)) & 0x0f];
        }
        return s;
      }
      function hex(x) { for (let i = 0; i < x.length; i++) x[i] = rhex(x[i]); return x.join(''); }
      function add32(a, b) { return (a + b) & 0xffffffff; }
      return hex(md51(string));
    },
  },
};
</script>
